/**
 * OfferListRow — one row in the flat list view.
 *
 * Pricing fields use `fontVariantNumeric: 'tabular-nums'` (CSS `tnum`) so
 * column digits line up across rows even without the explicit grid. Actions
 * are right-aligned and respect the 44px touch-target floor.
 */

import ChatBubbleOutlinedIcon from '@mui/icons-material/ChatBubbleOutlined';
import { Box, GlassButton, GlassChip, Typography, useI18n } from '@ops-matrix/design-system-ui';
import { OfferCellValue } from './OfferCellValue';
import type { OfferRecord } from './types';

export interface OfferListRowProps {
  offer: OfferRecord;
  isLast: boolean;
  onView: (offer: OfferRecord) => void;
  onAccept: (offer: OfferRecord) => void;
  onCounter: (offer: OfferRecord) => void;
  onAsk: (offer: OfferRecord) => void;
}

function statusChip(status: OfferRecord['status'], label: string) {
  if (status === 'accepted') {
    return <GlassChip label={label} size="small" color="success" variant="filled" />;
  }
  if (status === 'rejected') {
    return <GlassChip label={label} size="small" color="error" variant="outlined" />;
  }
  if (status === 'expired') {
    return <GlassChip label={label} size="small" color="warning" variant="outlined" />;
  }
  return <GlassChip label={label} size="small" color="primary" variant="outlined" />;
}

export function OfferListRow({ offer, isLast, onView, onAccept, onCounter, onAsk }: OfferListRowProps) {
  const { t } = useI18n();
  const canRespond = offer.status === 'pending';

  // Localized status label — fallback to the raw enum value so the chip
  // still reads sensibly while keys are being added by the i18n agent.
  const statusLabel = t(`status.${offer.status}`);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.5,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
        minHeight: 64,
      }}
    >
      {/* Lender block */}
      <Box sx={{ minWidth: 200, flex: '1 1 200px' }}>
        <OfferCellValue fieldKey="lender" offer={offer} />
      </Box>

      {/* Headline pricing — three tight cells */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'baseline', minWidth: 0 }}>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('consumer.offers.compare.amount')}
          </Typography>
          <OfferCellValue fieldKey="amount" offer={offer} />
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('consumer.offers.compare.rate')}
          </Typography>
          <OfferCellValue fieldKey="rate" offer={offer} />
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('consumer.offers.compare.monthly')}
          </Typography>
          <OfferCellValue fieldKey="monthly" offer={offer} />
        </Box>
      </Box>

      {/* Status + deadline */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 'auto', alignItems: 'flex-end' }}>
        {statusChip(offer.status, statusLabel)}
        {offer.deadline && canRespond && (
          <Typography variant="caption" sx={{ color: 'error.main' }}>
            {t('consumer.offers.decideby')} {offer.deadline}
          </Typography>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <GlassButton size="small" variant="outlined" onClick={() => onView(offer)} sx={{ minHeight: 44 }}>
          {t('common.view')}
        </GlassButton>
        {canRespond && (
          <>
            <GlassButton size="small" variant="outlined" onClick={() => onCounter(offer)} sx={{ minHeight: 44 }}>
              {t('consumer.offers.counter')}
            </GlassButton>
            <GlassButton
              size="small"
              variant="text"
              startIcon={<ChatBubbleOutlinedIcon />}
              onClick={() => onAsk(offer)}
              sx={{ minHeight: 44 }}
            >
              {t('consumer.offers.askAstor')}
            </GlassButton>
            <GlassButton
              size="small"
              variant="glow"
              color="success"
              onClick={() => onAccept(offer)}
              sx={{ minHeight: 44 }}
            >
              {t('consumer.offers.accept')}
            </GlassButton>
          </>
        )}
      </Box>
    </Box>
  );
}

OfferListRow.displayName = 'OfferListRow';
